/**
 * Explore v2 — constants
 *
 * Self-contained config for the public discovery feed (`/explore-v2`).
 * Sort + filter keys map 1:1 onto the backend `GET /api/v2/wheel/explore/`
 * boolean query params (see `yuga_wheel/.../explore/explore.py`):
 *   sort flags  → most_recent | most_viewed | clones | favorites |
 *                 alphabetic_asc | alphabetic_desc | most_unlocked
 *   filter flags→ is_clonable | is_locked | favorited_creators | paid_lock
 *
 * U12 (design-lock N17, ledger DL-088): SORT_OPTIONS + FILTER_OPTIONS below
 * feed the single unified <SortFilterMenu> ("Sort & Filter" funnel popover)
 * that replaced the legacy separate Sort dropdown + Filter chip row — see
 * ExploreToolbar.jsx. Adding an entry here is enough to add a row to BOTH
 * columns; no popover-layout change needed.
 *
 * The special TRENDING sort sends NO sort flag. With the backend's
 * EXPLORE_V2_ENABLED flag ON, "no explicit sort" yields the pre-computed
 * scored "Trending" order (already excludes flagged / private / quarantined
 * wheels by construction — explore-exclusion work #1137). With the flag OFF
 * the backend falls back to its legacy default ordering — the UI is correct
 * either way because it never depends on the ordering itself.
 */
import { PAID_LOCKS_ENABLED } from "../../constants/featureFlags";

// Page size requested per infinite-scroll page (backend max_page_size = 100).
export const EXPLORE_PAGE_SIZE = 20;

// Sort options shown in the Sort menu. `param: null` => Trending (no flag).
export const SORT_OPTIONS = [
  { value: "trending", label: "Trending", param: null },
  { value: "most_recent", label: "Newest", param: "most_recent" },
  { value: "most_viewed", label: "Most viewed", param: "most_viewed" },
  { value: "clones", label: "Most cloned", param: "clones" },
  { value: "favorites", label: "Most favorited", param: "favorites" },
  { value: "alphabetic_asc", label: "A → Z", param: "alphabetic_asc" },
  { value: "alphabetic_desc", label: "Z → A", param: "alphabetic_desc" },
];

export const DEFAULT_SORT = SORT_OPTIONS[0].value; // "trending"

// Boolean filter checkboxes (Filter by). `param` is the backend query flag
// set to "true". N17: Paid Lock — wheels locked specifically via a
// PaymentLock (a strict subset of "Locked"; access-code/permission/data-entry
// locks don't count) — needs Backend PR (paid_lock query param, U12).
//
// ISP-9999 (gordon-0918-staging-hide-paid-locks-and-segment-locks-keep-wheel-locks):
// the "Paid Lock" chip is gated on PAID_LOCKS_ENABLED (OFF on staging today) — Paid
// Locks aren't ready for staging, so the filter for them must not be choosable there.
export const FILTER_OPTIONS = [
  { value: "is_clonable", label: "Clonable", param: "is_clonable" },
  { value: "is_locked", label: "Locked", param: "is_locked" },
  ...(PAID_LOCKS_ENABLED
    ? [{ value: "paid_lock", label: "Paid Lock", param: "paid_lock" }]
    : []),
];

// Debounce (ms) before a typed search term hits the API.
export const SEARCH_DEBOUNCE_MS = 500;

// EW-1181 — hard cap on the search field length. A pathological query (e.g. a
// pasted 10k-char string) otherwise fails at the network layer with no UI
// feedback. 200 chars is far beyond any legitimate wheel-name search yet well
// under any backend/query limit, so the input is capped client-side.
export const SEARCH_MAX_LENGTH = 200;

// Defensive clamp used by the toolbar's change handler. The native input
// `maxLength` already blocks typed/pasted overflow, but this guarantees the
// debounced query never exceeds the cap regardless of how the value was set,
// and tolerates a non-string argument. Pure → unit-tested.
export const clampSearchText = (value) => {
  const s = value == null ? "" : String(value);
  return s.length > SEARCH_MAX_LENGTH ? s.slice(0, SEARCH_MAX_LENGTH) : s;
};

// DL-023 — the 16 LOCKED curated Explore categories (Gordon, one word each,
// App-Store style). Fixed set, hardcoded FE-side: the backend exposes
// `?category=<slug>` on GET /api/v2/wheel/explore/ (Backend PR #1449, re-cut
// #1470 — `yuga_wheel/models.py` `WheelCategory` TextChoices) but publishes no
// categories-list endpoint, so this array IS the source of truth for both the
// slug values sent to the API and the chip labels shown to the user. Replaces
// the old broken auto-tag row (P2-R9 — AI-hallucinated top-tags strings that
// dead-ended every filter click at "No wheels found").
//
// ISP-9999 (build-education-category-discover-wheel-tags, Kelly via Gordon
// 2026-09-08 09:40 PT, DECIDED — C:/iYuga/GORDON-DECISIONS.md): the set above
// was locked at 15 (see the 2026-08 lock decision this same file used to
// cite) — this decision unlocks it for "Education" only, appended at the end
// so every existing slug's position is untouched. This same constant backs
// BOTH surfaces the ticket names: the Explore/Discover category chip row
// (ExploreV2/index.jsx's CATEGORY_ROW_ITEMS) AND the wheel builder's
// Categories picker (SegmentBuilder/Main/DetailsTab/index.jsx imports
// EXPLORE_CATEGORIES directly), so one entry here covers both.
export const EXPLORE_CATEGORIES = [
  { slug: "wellness", label: "Wellness" },
  { slug: "coaching", label: "Coaching" },
  { slug: "business", label: "Business" },
  { slug: "fitness", label: "Fitness" },
  { slug: "travel", label: "Travel" },
  { slug: "family", label: "Family" },
  { slug: "pets", label: "Pets" },
  { slug: "food", label: "Food" },
  { slug: "beauty", label: "Beauty" },
  { slug: "creative", label: "Creative" },
  { slug: "home", label: "Home" },
  { slug: "events", label: "Events" },
  { slug: "faith", label: "Faith" },
  { slug: "finance", label: "Finance" },
  { slug: "portfolio", label: "Portfolio" },
  { slug: "education", label: "Education" },
];

// Sentinel for "no category filter" (the leading "All" chip). Not a real
// backend slug/value — when selected, the `category` query param is omitted
// entirely so the feed is unfiltered.
export const EXPLORE_ALL_CATEGORY = "all";

// Onboarding "Pick a few interests" taxonomy (Gordon 2026-08-06). A VIEW of
// EXPLORE_CATEGORIES, not a second source of truth: the DL-023 locked set above
// stays byte-for-byte untouched (still exactly 15, still drives the Explore
// feed's category filter tabs and its own invariant tests in
// constants.categories.test.js) — only the onboarding prompt gets "Portfolio"
// retired and the remaining categories shown alphabetically. Deriving this
// instead of hand-listing it means a future add/remove/rename to the locked
// set is inherited automatically instead of silently drifting out of sync.
export const ONBOARDING_INTEREST_CATEGORIES = EXPLORE_CATEGORIES.filter(
  (c) => c.slug !== "portfolio"
)
  .slice()
  .sort((a, b) => a.label.localeCompare(b.label));

// DL-024 — Showcase Rail feature flag (CRA env bool, DEFAULT OFF). When absent or
// not exactly "true", the Trending showcase rail never renders and ExploreV2 is
// byte-for-byte identical to today. Flip REACT_APP_EXPLORE_SHOWCASE=true (build-
// time) to enable it. Verifiable flag-OFF (control) + flag-ON parity.
export const SHOWCASE_ENABLED =
  String(process.env.REACT_APP_EXPLORE_SHOWCASE).toLowerCase() === "true";
